import type { LucideIcon } from 'lucide-react';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert } from './Alert';
import { Button } from './Button';
import { EmptyState } from './EmptyState';
import { SkeletonRows } from './Skeleton';
import { cn } from '@/utils/cn';

export interface Column<T> {
  key: string;
  header: string;
  render: (row: T) => ReactNode;
  /** Aligns to the inline end (numbers, actions), so it flips with direction. */
  align?: 'start' | 'end';
  /** Marks the column that titles the row when it collapses into a card on small screens. */
  primary?: boolean;
  /** Omitted from the stacked card view — useful for a column already shown as the card title. */
  hideOnCard?: boolean;
  headerClassName?: string;
  cellClassName?: string;
}

interface DataTableProps<T> {
  columns: Column<T>[];
  rows: T[] | undefined;
  rowKey: (row: T) => string;
  isLoading?: boolean;
  isError?: boolean;
  errorMessage?: string;
  onRetry?: () => void;
  emptyTitle: string;
  emptyDescription?: string;
  emptyIcon?: LucideIcon;
  /** A way out of an empty result, such as clearing the filters that caused it. */
  emptyAction?: ReactNode;
  /** Accessible description of the table. */
  caption: string;
  /** Row actions for the stacked card, shown at its foot; the table keeps its own actions column, which is usually `hideOnCard`. */
  cardActions?: (row: T) => ReactNode;
}

/**
 * Table with the four states every list needs: loading, error, empty and data.
 *
 * Below `md` each row collapses into a stacked card. A phone cannot show six columns, and
 * horizontally scrolling a table is a poor experience for a salesperson on a handset — which this
 * product explicitly targets.
 */
export function DataTable<T>({
  columns,
  rows,
  rowKey,
  isLoading = false,
  isError = false,
  errorMessage,
  onRetry,
  emptyTitle,
  emptyDescription,
  emptyIcon,
  emptyAction,
  caption,
  cardActions,
}: DataTableProps<T>) {
  const { t } = useTranslation();

  if (isLoading) {
    return <SkeletonRows rows={4} columns={Math.min(columns.length, 5)} />;
  }

  if (isError) {
    return (
      <div className="p-5">
        <Alert tone="error">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <span>{errorMessage ?? t('common.loadError')}</span>
            {onRetry ? (
              <Button variant="secondary" size="sm" onClick={onRetry}>
                {t('common.retry')}
              </Button>
            ) : null}
          </div>
        </Alert>
      </div>
    );
  }

  if (!rows || rows.length === 0) {
    return (
      <EmptyState
        icon={emptyIcon}
        title={emptyTitle}
        description={emptyDescription}
        action={emptyAction}
      />
    );
  }

  const primary = columns.find((column) => column.primary) ?? columns[0];

  return (
    <>
      {/* Table from md upwards. */}
      <div className="hidden overflow-x-auto md:block">
        <table className="w-full text-sm">
          <caption className="sr-only">{caption}</caption>
          <thead className="border-line text-muted border-b">
            <tr>
              {columns.map((column) => (
                <th
                  key={column.key}
                  scope="col"
                  className={cn(
                    'px-5 py-3 font-medium whitespace-nowrap',
                    column.align === 'end' ? 'text-end' : 'text-start',
                    column.headerClassName,
                  )}
                >
                  {column.header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr
                key={rowKey(row)}
                className="border-line hover:bg-canvas/60 border-b last:border-0"
              >
                {columns.map((column) => (
                  <td
                    key={column.key}
                    className={cn(
                      'text-ink px-5 py-3.5',
                      column.align === 'end' ? 'text-end' : 'text-start',
                      column.cellClassName,
                    )}
                  >
                    {column.render(row)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Stacked cards below md. */}
      <ul className="divide-line divide-y md:hidden">
        {rows.map((row) => (
          <li key={rowKey(row)} className="px-4 py-4">
            <div className="text-ink mb-2 text-sm font-medium">{primary?.render(row)}</div>

            <dl className="space-y-1.5">
              {columns
                .filter((column) => column !== primary && !column.hideOnCard)
                .map((column) => (
                  <div key={column.key} className="flex items-start justify-between gap-3">
                    <dt className="text-muted shrink-0 text-xs">{column.header}</dt>
                    <dd className="text-ink min-w-0 text-end text-sm">{column.render(row)}</dd>
                  </div>
                ))}
            </dl>

            {cardActions ? (
              <div className="mt-3 flex flex-wrap justify-end gap-2" data-testid="card-actions">
                {cardActions(row)}
              </div>
            ) : null}
          </li>
        ))}
      </ul>
    </>
  );
}
